import loadAsset from "./includes/assets-loader"
import { loadJSON } from "./utils"

const arePassiveListenersSupported = () => {
  let passiveSupported = false

  try {
    window.addEventListener(
      "test",
      null,
      Object.defineProperty({}, "passive", {
        get: () => (passiveSupported = true),
      }),
    )
  } catch (err) { }

  return passiveSupported
}

export default class Toptimize {
  constructor(options) {
    const self = this

    self.options = {
      attr: "data-component-assets",
      observerLandscapeOffset: 0.4, // 0-1 (1 = 100% of the window height) or "200px" (string)
      observerPortraitOffset: 0.25, // 0-1 (1 = 100% of the window height) or "200px" (string)
      manifestUrl: "/assets/manifest.json",
      assetsRootUrl: '',
      cacheVersion: null,
      ...options,
    }

    self.winHeight = window.innerHeight || document.documentElement.clientHeight
    self.isLandscape =
      self.winHeight <=
      (window.innerWidth || document.documentElement.clientWidth)

    self.init()
  }

  init() {
    const self = this
    const w = window
    const d = document

    const observe = (el, assets, callback) => {
      const observer = new IntersectionObserver(
        (entries, observer) => {
          for (var i = 0; i < entries.length; i++) {
            const e = entries[i]
            const element = e.target

            if (
              e.isIntersecting &&
              element.offsetParent &&
              window.getComputedStyle(element).display !== "none"
            ) {
              observer.disconnect()

              setTimeout(() => callback(el, assets))
            }
          }
        },
        {
          rootMargin: `${typeof self.options.observerOffset === "string"
            ? self.options.observerOffset
            : Math.round(
              self.winHeight *
              (self.isLandscape
                ? self.options.observerLandscapeOffset
                : self.options.observerPortraitOffset),
            ) + "px"
            } 0px`,
        },
      )

      observer.observe(el)
    }

    const assetsToObject = (assetsDatas, manifest) => {
      const elems = []

      return assetsDatas.reduce((prev, assetsData) => {
        return ["css", "js"].reduce((prev1, type) => {
          const d = assetsData[type]

          if (!d) return { ...prev }

          if (!elems.some((asset) => asset.el === assetsData.el)) {
            elems.push({
              el: assetsData.el,
              id: d.id,
            })
          }

          if (!manifest[d.id]) {
            throw `"${d.id
            }" file does't exist in the "assets" dir. See available: ${Object.keys(
              manifest,
            )
              .filter((assetFile) => {
                const arr = assetFile.split(".")
                return ["css", "js"].indexOf(arr[arr.length - 1]) > -1
              })
              .join(", ")}`
          }

          return {
            ...prev1,
            [type]: d
              ? {
                ...prev[type],
                [d.id]: {
                  ...d,
                  ...{
                    el: elems.map(({ el }) => el),
                    url: `${self.options.assetsRootUrl}${manifest[d.id]}${self.options.cacheVersion ? `?ver=${self.options.cacheVersion}` : ''}`,
                  },
                },
              }
              : { ...prev[type] },
          }
        }, {})
      }, {})
    }

    const getAssetData = (el, o) => {
      return Object.values(o).find(
        (obj) => Array.isArray(obj.el) && obj.el.indexOf(el) > -1,
      )
    }

    const loadJS = (el, assetsObj) => {
      const asset = getAssetData(el, assetsObj.js)

      !!asset &&
        new loadAsset({
          moduleName: asset.id,
          assets: assetsObj.js,
          type: "js",
        })
    }

    const reveal = (el, assetsObj) => {
      const asset = getAssetData(el, assetsObj.css)

      if (!!asset) {
        // Load CSS first
        return new loadAsset({
          moduleName: asset.id,
          assets: assetsObj.css,
        }).then(() => loadJS(el, assetsObj))
      }

      loadJS(el, assetsObj)
    }

    // On manifest file load
    const components = [].slice.call(
      d.querySelectorAll(`[${self.options.attr}]`),
    )

    if (components.length === 0) return

    loadJSON(self.options.manifestUrl, (manifest) => {
      const main = components.map((el) => ({
        ...{ el },
        ...(el.dataset.componentAssets
          ? JSON.parse(el.dataset.componentAssets)
          : {}),
      }))

      const deps = []
      main.forEach((scheme) => {
        ;["css", "js"].forEach((type) => {
          if (
            scheme[type] &&
            scheme[type].deps &&
            scheme[type].deps.length > 0
          ) {
            scheme[type].deps.forEach((sc) => {
              deps.push({ [type]: { id: sc, deps: [] } })
            })
          }
        })
      })

      const mainObj = assetsToObject(main, manifest)
      const depsObj = assetsToObject(deps, manifest)
      const assetsObj = ["css", "js"].reduce(
        (prev, type) => ({
          ...prev,
          [type]: {
            ...depsObj[type],
            ...mainObj[type],
          },
        }),
        {},
      )

      components.forEach((el) => !!el && observe(el, assetsObj, reveal))

      const injectAsset = (src, as) => {
        const s = d.createElement("link")
        s.rel = "preload"
        s.as = as
        s.href = src
        d.head.appendChild(s)
      }

      // Preload assets on scroll down by > 0

      let assetsPreloadingStarted = false

      const preloadAssets = () => {
        if (w.scrollY > 0 && !assetsPreloadingStarted) {
          ;[
            { type: "style", arr: Object.values(assetsObj.css) },
            { type: "script", arr: Object.values(assetsObj.js) },
          ].forEach((item) =>
            item.arr.forEach(
              (s) =>
                s.url &&
                !s.hasOwnProperty("loading") &&
                injectAsset(s.url, item.type),
            ),
          )

          w.removeEventListener("scroll", preloadAssets)
          assetsPreloadingStarted = true
        }
      }

      preloadAssets()
      w.addEventListener(
        "scroll",
        preloadAssets,
        arePassiveListenersSupported() ? { passive: true } : false,
      )
    })
  }
}
